import React from "react";
import * as Yup from "yup";
import Validation from "shared/form/Validation";
import { useCopies } from "shared/hooks/useCopies";
import { getFormattedCurrency } from "shared/utils";
import { BulkTicketQuantityFormPropTypes } from "./types";
import { useCommonFeatures } from "context/commonFeatures";
import { isInvoicePaymentRequestedAndPending } from "apps/myExperience/utils";
import { getOptionsForBulkQuantity } from "shared/components/bulkQuantityPriceBreakUpMap/util";

export function useBulkTicketQuantityFormService({ initPurchaseHandler, ...props }: BulkTicketQuantityFormPropTypes) {
  const copies = useCopies();
  const { initConfirmationAlert } = useCommonFeatures();
  const [isQuantityChanged, setIsQuantityChanged] = React.useState(false);

  const isInvoicePaymentPending = React.useMemo(
    () => isInvoicePaymentRequestedAndPending(props.pendingPO),
    [props.pendingPO]
  );

  const quantityOptions = React.useMemo(
    () =>
      getOptionsForBulkQuantity({
        applicableRates: props.applicableRates,
        perTicketPriceStr: copies.get("bulk.tickets.per.ticket.price.option")
      }).map(item => ({
        label: item.value,
        value: item.id,
        customData: item.customData
      })),
    [props.applicableRates, copies]
  );

  const initialValues = React.useMemo(() => {
    return {
      quantity:
        props.pendingPO?.lines?.reduce((acc, currLine) => acc + currLine.quantity, 0) ||
        props.selectedQuantity ||
        quantityOptions?.[0]?.value
    };
  }, [props.pendingPO?.lines, props.selectedQuantity, quantityOptions]);

  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      quantity: Validation.integerValidation({
        required: true,
        copies
      })
    });
  }, [copies]);

  const getApplicableMeetingPrice = React.useCallback(
    quantity => quantityOptions.find(i => i.value === quantity)?.customData,
    [quantityOptions]
  );

  const getApplicableTotalPrice = React.useCallback(
    quantity => {
      const unitPrice = getApplicableMeetingPrice(quantity);
      if (unitPrice) {
        const total = unitPrice.value * quantity;
        return getFormattedCurrency({ value: total, currencyCode: unitPrice.currencyCode });
      }
    },
    [getApplicableMeetingPrice]
  );

  const quantityChangeHandler = React.useCallback(
    (quantity: number | string) => {
      setIsQuantityChanged(initialValues.quantity !== Number(quantity));
    },
    [initialValues.quantity]
  );

  const requestUpdateClickHandler = React.useCallback(
    values => {
      initConfirmationAlert({
        message: copies.get("invoice.payment.buy.tickets.quantity.update.confirmation"),
        handleConfirm: () => {
          initPurchaseHandler(values);
        }
      });
    },
    [copies, initConfirmationAlert, initPurchaseHandler]
  );

  const submitHandler = React.useCallback(
    values => {
      isInvoicePaymentPending && isQuantityChanged
        ? requestUpdateClickHandler({ ...values, ticketFormId: props.ticketFormId })
        : initPurchaseHandler({ ...values, ticketFormId: props.ticketFormId });
    },
    [isInvoicePaymentPending, isQuantityChanged, requestUpdateClickHandler, initPurchaseHandler, props.ticketFormId]
  );

  return {
    submitHandler,
    initialValues,
    quantityOptions,
    validationSchema,
    isQuantityChanged,
    setIsQuantityChanged,
    quantityChangeHandler,
    isInvoicePaymentPending,
    getApplicableTotalPrice,
    getApplicableMeetingPrice
  };
}
